import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  CORE_INVENTORY_ITEMS,
  INVENTORY_CATALOG_CATEGORIES,
  getCatalogSourceUrl,
  localizeCatalogName,
  poundsToKilograms,
} from "../../catalogs/inventoryCatalog";
import DraggableModal from "../../components/layout/DraggableModal/DraggableModal";

const formatNumber = (value) => Number(value).toLocaleString(undefined, {
  maximumFractionDigits: 2,
});

const InventoryCatalogModal = ({ isOpen, onClose, onSelect }) => {
  const { i18n, t } = useTranslation();
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [proficiency, setProficiency] = useState("");
  const [weaponType, setWeaponType] = useState("");
  const language = (i18n.resolvedLanguage || i18n.language) === "en" ? "en" : "ru";

  const items = useMemo(() => {
    const query = search.trim().toLocaleLowerCase();
    return CORE_INVENTORY_ITEMS.filter((item) => {
      const matchesSearch = !query || `${item.name.en} ${item.name.ru}`
        .toLocaleLowerCase()
        .includes(query);
      const matchesCategory = !category || item.category === category;
      const matchesProficiency = !proficiency || item.proficiency === proficiency;
      const matchesWeaponType = !weaponType || item.weaponType === weaponType;
      return matchesSearch && matchesCategory && matchesProficiency && matchesWeaponType;
    });
  }, [category, proficiency, search, weaponType]);

  if (!isOpen) return null;

  return (
    <DraggableModal
      className="inventory-catalog-modal"
      headerText={t("inventory.catalog.title")}
      isVisible={isOpen}
      onClose={onClose}
    >
      <div className="inventory-catalog-controls">
        <label className="inventory-catalog-search">
          <span>{t("inventory.catalog.search")}</span>
          <input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t("inventory.catalog.searchPlaceholder")}
          />
        </label>
        <div className="inventory-catalog-filters">
          <label>
            <span>{t("inventory.catalog.category")}</span>
            <select value={category} onChange={(event) => setCategory(event.target.value)}>
              <option value="">{t("inventory.catalog.allCategories")}</option>
              {INVENTORY_CATALOG_CATEGORIES.map((value) => (
                <option key={value} value={value}>
                  {t(`inventory.catalog.categories.${value}`)}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span>{t("inventory.catalog.proficiency")}</span>
            <select value={proficiency} onChange={(event) => setProficiency(event.target.value)}>
              <option value="">{t("inventory.catalog.allProficiencies")}</option>
              {["simple", "martial", "exotic"].map((value) => (
                <option key={value} value={value}>
                  {t(`inventory.catalog.proficiencies.${value}`)}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span>{t("inventory.catalog.weaponMode")}</span>
            <select value={weaponType} onChange={(event) => setWeaponType(event.target.value)}>
              <option value="">{t("inventory.catalog.allWeaponModes")}</option>
              <option value="melee">{t("system.weaponTypes.melee")}</option>
              <option value="ranged">{t("system.weaponTypes.ranged")}</option>
            </select>
          </label>
        </div>
      </div>

      <p className="inventory-catalog-count" role="status">
        {t("inventory.catalog.found", { count: items.length })}
      </p>

      {items.length === 0 ? (
        <p className="inventory-catalog-empty">{t("inventory.catalog.empty")}</p>
      ) : (
        <div className="inventory-catalog-list">
          {items.map((item) => {
            const primaryName = localizeCatalogName(item, language);
            const secondaryName = localizeCatalogName(item, language === "en" ? "ru" : "en");
            return (
              <article className="inventory-catalog-card" key={item.id}>
                <div className="inventory-catalog-card-heading">
                  <div>
                    <h3>{primaryName}</h3>
                    <p>{secondaryName}</p>
                  </div>
                  <span>{t(`inventory.catalog.categories.${item.category}`)}</span>
                </div>
                <dl className="inventory-catalog-stats">
                  <div><dt>{t("inventory.catalog.price")}</dt><dd>{formatNumber(item.priceGp)} gp</dd></div>
                  <div><dt>{t("inventory.catalog.weight")}</dt><dd>{formatNumber(item.weightLb)} lb / {formatNumber(poundsToKilograms(item.weightLb))} kg</dd></div>
                  {item.damage && <div><dt>{t("inventory.catalog.damage")}</dt><dd>{item.damage} {item.damageCode}</dd></div>}
                  {item.critical && <div><dt>{t("inventory.catalog.critical")}</dt><dd>{item.critical}</dd></div>}
                  {item.armorClass > 0 && <div><dt>{t("inventory.catalog.armorBonus")}</dt><dd>+{item.armorClass}</dd></div>}
                  {item.rangeFt && <div><dt>{t("inventory.catalog.range")}</dt><dd>{item.rangeFt} ft.</dd></div>}
                </dl>
                <div className="inventory-catalog-card-actions">
                  <a href={getCatalogSourceUrl(item, language)} target="_blank" rel="noreferrer">
                    {t("inventory.catalog.source")}
                  </a>
                  <button type="button" onClick={() => onSelect(item)}>
                    {t("inventory.catalog.add")}
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </DraggableModal>
  );
};

export default InventoryCatalogModal;
